/** Server-side workflow JSON shape returned by GET /workflows/{id} */
export interface ServerWorkflow {
    workflowId: string;
    name: string;
    triggerType: string;
    entryNodeId: string;
    applyOncePerContact: boolean;
    status: string;
    revision: number;
    graph: ServerGraph;
    createdAt?: string;
    updatedAt?: string;
}

export interface ServerGraph {
    entryNodeId: string;
    nodes: ServerNode[];
    edges: ServerEdge[];
    version?: string;
    globals?: Record<string, unknown>;
}

export interface ServerNode {
    id: string;
    /** Dot-notation type e.g. "Trigger.Message.Inbound", "Condition.Time.RunSchedule", "Action.Crm.CreateLead" */
    type: string;
    config: Record<string, unknown>;
    position: { x: number; y: number };
}

export type EdgeCondition = "ALWAYS" | "TRUE" | "FALSE";

export interface ServerEdge {
    from: string;
    /** Target node id. null for dead-end FALSE paths. */
    to: string | null;
    when: EdgeCondition;
}

/**
 * Payload for POST /workflows (creation).
 * Does NOT include workflowId, status, revision — server generates those.
 */
export interface WorkflowCreatePayload {
    name: string;
    triggerType: string;
    applyOncePerContact: boolean;
    graph: ServerGraph;
}

/**
 * Payload for PUT /workflows/{id} (update).
 * Includes workflowId so the server knows which workflow to update.
 */
export interface WorkflowUpdatePayload extends WorkflowCreatePayload {
    workflowId: string;
    status?: string;
    revision?: number;
}

/** Union of both payloads for the serializer output */
export type WorkflowPayload = WorkflowCreatePayload | WorkflowUpdatePayload;

/** Metadata needed to serialise steps back into a workflow payload */
export interface WorkflowMetadata {
    workflowId?: string;
    name: string;
    triggerType: string;
    applyOncePerContact?: boolean;
    status?: string;
    revision?: number;
}

/** The three high-level node categories derived from the dot-notation type string */
export type NodeCategory = "trigger" | "condition" | "action";

export function classifyNodeType(serverType: string): NodeCategory {
    if (serverType.startsWith("Trigger.")) return "trigger";
    if (serverType.startsWith("Condition.")) return "condition";
    return "action";
}
